import React, { useEffect, useRef } from 'react';
import { useTickerLink } from '../contexts/TickerLinkContext';
import { LinkIcon } from './LinkIcon';

interface TickerLinkButtonProps {
  windowId: string;
  currentTicker: string;
  onTickerChange: (ticker: string) => void;
}

export const TickerLinkButton: React.FC<TickerLinkButtonProps> = ({
  windowId,
  currentTicker,
  onTickerChange,
}) => {
  const { linkedTicker, isWindowLinked, toggleWindowLink, setLinkedTicker } = useTickerLink();
  const isLinked = isWindowLinked(windowId);
  const prevLinkedTicker = useRef<string | null>(null);
  const prevCurrentTicker = useRef<string>(currentTicker);
  const isReceivingUpdate = useRef(false);

  // When linked ticker changes externally, update this widget
  useEffect(() => {
    // Skip if not linked or no linked ticker
    if (!isLinked || !linkedTicker) {
      prevLinkedTicker.current = linkedTicker;
      return;
    }

    // Only update if linkedTicker actually changed (not just a re-render)
    if (linkedTicker !== prevLinkedTicker.current && linkedTicker !== currentTicker) {
      // Mark that we're receiving an update so we don't broadcast it back
      isReceivingUpdate.current = true;
      onTickerChange(linkedTicker);
    }

    prevLinkedTicker.current = linkedTicker;
  }, [linkedTicker, isLinked, onTickerChange, currentTicker]);

  // When this widget's ticker changes locally (user interaction), broadcast to others
  useEffect(() => {
    // Skip if we're receiving an update from the linked context
    if (isReceivingUpdate.current) {
      isReceivingUpdate.current = false;
      prevCurrentTicker.current = currentTicker;
      return;
    }

    // Only broadcast if ticker actually changed and we're linked
    if (
      isLinked &&
      currentTicker &&
      currentTicker !== prevCurrentTicker.current &&
      currentTicker !== linkedTicker
    ) {
      setLinkedTicker(currentTicker);
    }

    prevCurrentTicker.current = currentTicker;
  }, [currentTicker, isLinked, linkedTicker, setLinkedTicker]);

  const handleClick = () => {
    toggleWindowLink(windowId);
    // If becoming linked and there's already a linked ticker, adopt it
    if (!isLinked && linkedTicker) {
      isReceivingUpdate.current = true;
      onTickerChange(linkedTicker);
    }
  };

  return (
    <button
      onClick={handleClick}
      aria-pressed={isLinked}
      className={`p-1 rounded transition-colors ${
        isLinked
          ? 'text-accent-yellow bg-accent-yellow/10 hover:bg-accent-yellow/20'
          : 'text-gray-400 hover:text-white hover:bg-dark-surface-light'
      }`}
      title={
        isLinked
          ? `Ticker synced with other linked widgets${linkedTicker ? ` (${linkedTicker})` : ''} — click to unlink`
          : 'Sync this ticker with other linked widgets'
      }
    >
      <LinkIcon linked={isLinked} />
    </button>
  );
};
